// =============================================================================
// web/src/features/pos/poPdf.ts — the Coretel purchase order PDF
// =============================================================================
// 27 Oct 2026 — rebuilt against HVAC Australia's PO to us (70035174), which
// James put on the desk: "went to send one and just not happy with it".
// Their bones, Coretel's look, our line table kept:
//   Coretel block (logo, address, ABN) · PURCHASE ORDER / number / date /
//   APPROVED · Supplier and Deliver-to boxes · "TAX INVOICES TO" banner · the
//   strip (project · site · order date · deliver by · payment terms) · lines ·
//   scope beside Net / GST / Total · one quiet row (raised by · Coretel
//   contact · insurances) · the terms line.
// No budget code on the supplier's copy (that is ours). No warranty box
// (James). The terms line says the supplier works to the SITE's HSE
// requirements and brings its own safety paper — Coretel does not certify a
// crane company's lift plan, it receives it.
//
// The PDF is saved to storage bucket po-pdfs/{po_id}/{number}_v{n}.pdf
// (mig 464) and the PO row stamped (pdf_storage_path / pdf_version /
// pdf_generated_*), the way quotePdf does — so the worker can attach the
// same file to the email, and a supplier who loses their copy gets the same
// one again.
// =============================================================================

import { supabase } from "@/lib/supabase";
import { getPdfMake, fetchLogoDataUrl, PDF_COLOR as COLOR, PDF_COMPANY as COMPANY } from "@/features/quotes/quotePdf";

// 27 Oct 2026 — James: accounts@ is remittance; AP (where a supplier sends
// their invoice) is invoices@coretelgroup.com. Everything on the PO says so.
export const PO_INVOICES_EMAIL = "invoices@coretelgroup.com";

const moneyFmt = new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD", minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtMoney = (n: number | null | undefined) => moneyFmt.format(Number(n ?? 0));
function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso); if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "numeric" });
}

export const PO_KIND_LABEL: Record<string, string> = { plant: "Plant / hire", materials: "Materials", labour: "Labour / subcontract", other: "Other" };
export const DELIVER_KIND_LABEL: Record<string, string> = { site: "Site", depot: "Depot", office: "Depot", other: "Other" };

interface PoLine { description: string | null; quantity: number | null; unit: string | null; unit_price_ex_gst: number | null; line_total_ex_gst: number | null }
interface PoFull {
  po_id: string; po_number: string; po_title: string | null; status: string; po_date: string | null; required_by_date: string | null;
  subtotal_ex_gst: number; gst_amount: number; total_inc_gst: number; scope_text: string | null; notes_text: string | null;
  po_kind: string; deliver_to_kind: string; show_supplier_contact?: boolean | null; deliver_to_text: string | null; site_contact_name: string | null; site_contact_phone: string | null;
  payment_terms_text: string | null; coretel_contact_name: string | null; coretel_contact_phone: string | null;
  approved_at: string | null; sent_at: string | null; pdf_version: number | null;
  supplier: any; project: any; site: any; lines: PoLine[];
  settings: Record<string, string>;
}

export interface GeneratePoPdfResult { signedUrl: string; storagePath: string; version: number }

/** The settings rows the PDF prints (mig 464). */
export async function loadPoSettings(): Promise<Record<string, string>> {
  const { data } = await (supabase as any).from("finance_settings").select("key, value")
    .in("key", ["company_office_address", "company_depot_address", "po_default_payment_terms", "po_terms_line", "po_insurances_line"]);
  return Object.fromEntries(((data ?? []) as any[]).map(r => [r.key, r.value ?? ""]));
}

/** Where a PO delivers by default — James's rule: plant → site, materials → depot, labour → office. */
export function defaultDeliverKind(poKind: string): "site" | "depot" | "office" {
  if (poKind === "plant") return "site";
  if (poKind === "materials") return "depot";
  if (poKind === "labour") return "depot";   // 27 Oct: "the address needs to be the depot not my house"
  return "site";
}

/** The printed deliver-to for a quick pick. */
export function deliverTextFor(kind: string, site: any, settings: Record<string, string>): string {
  if (kind === "depot" || kind === "office") return `Coretel Group — depot\n${settings.company_depot_address ?? COMPANY.address}`;
  if (kind === "site" && site) {
    const addr = [site.address_line1, site.address_line2, [site.suburb, site.state, site.postcode].filter(Boolean).join(" ")].filter(Boolean).join(", ");
    // the site's NAME — our CG-SITE code means nothing to a supplier (James, 27 Oct)
    return `${site.site_name ?? "Site"}\n${addr || "address on the site record"}`;
  }
  return "";
}

// -----------------------------------------------------------------------------
// Document definition
// -----------------------------------------------------------------------------
function buildDocDefinition(po: PoFull, logoDataUrl: string | null, raisedBy: string): any {
  const C = { ...COLOR, wash: COLOR.primaryWash, alt: COLOR.rowAlt };
  const s = po.settings;
  const supplierName = po.supplier?.trading_name ?? po.supplier?.legal_name ?? "—";
  const supplierLines = [
    supplierName,
    po.supplier?.legal_name && po.supplier?.trading_name && po.supplier.trading_name !== po.supplier.legal_name ? po.supplier.legal_name : null,
    po.supplier?.abn ? `ABN ${po.supplier.abn}` : null,
    [po.supplier?.address_line1, po.supplier?.address_line2, [po.supplier?.suburb, po.supplier?.state, po.supplier?.postcode].filter(Boolean).join(" ")].filter(Boolean).join(", ") || null,
  ].filter(Boolean) as string[];
  if (po.show_supplier_contact !== false) {
    const cName = po.supplier?.primary_contact_name, cEmail = po.supplier?.primary_contact_email ?? po.supplier?.billing_email, cPhone = po.supplier?.primary_contact_phone;
    if (cName) supplierLines.push(`Attn: ${cName}`);
    if (cEmail || cPhone) supplierLines.push([cEmail, cPhone].filter(Boolean).join("  ·  "));
  }

  const deliverLines = (po.deliver_to_text?.trim() || deliverTextFor(po.deliver_to_kind, po.site, s) || "—").split("\n");
  if (po.site_contact_name || po.site_contact_phone) deliverLines.push(`Site contact: ${[po.site_contact_name, po.site_contact_phone].filter(Boolean).join(" ")}`);

  const box = (title: string, lines: string[]) => ({
    table: { widths: ["*"], body: [[{ stack: [
      { text: title.toUpperCase(), fontSize: 8, bold: true, color: C.primary, characterSpacing: 1, margin: [0, 0, 0, 4] },
      ...lines.map(t => ({ text: t, fontSize: 9.5, color: C.text, lineHeight: 1.25 })),
    ], margin: [8, 6, 8, 6] }]] },
    layout: { hLineColor: () => C.ruleDark, vLineColor: () => C.ruleDark, hLineWidth: () => 0.6, vLineWidth: () => 0.6 },
  });
  const cell = (t: string, o: any = {}) => ({ text: t, fontSize: 9, color: C.text, margin: [6, 4, 6, 4], ...o });
  const hd = (t: string, o: any = {}) => ({ text: t, fontSize: 7.5, bold: true, color: C.muted, fillColor: C.alt, margin: [6, 4, 6, 4], ...o });
  const th = (t: string, o: any = {}) => ({ text: t, fontSize: 8, bold: true, color: "#ffffff", fillColor: C.primary, margin: [6, 5, 6, 5], ...o });
  const grid = { hLineColor: () => C.ruleDark, vLineColor: () => C.ruleDark, hLineWidth: () => 0.6, vLineWidth: () => 0.6 };

  const projectRef = [po.project?.project_code, po.project?.project_name].filter(Boolean).join(" · ") || "—";
  const siteRef = po.site?.site_name ?? "—";
  const status = po.approved_at || po.sent_at || po.status !== "draft" ? "APPROVED" : "DRAFT";

  const lineRows = po.lines.length ? po.lines.map((l, i) => {
    const qty = Number(l.quantity ?? 0), price = Number(l.unit_price_ex_gst ?? 0);
    const total = l.line_total_ex_gst != null ? Number(l.line_total_ex_gst) : qty * price;
    return [
      cell(String(i + 1), { color: C.muted, alignment: "right" }),
      cell(l.description ?? "—", { fontSize: 10 }),
      cell(qty.toLocaleString("en-AU", { maximumFractionDigits: 2 }), { alignment: "right" }),
      cell(l.unit ?? "", { color: C.muted }),
      cell(fmtMoney(price), { alignment: "right" }),
      cell(fmtMoney(total), { alignment: "right", bold: true }),
    ];
  }) : [[{ text: "(no lines)", fontSize: 10, color: C.muted, colSpan: 6, alignment: "center", margin: [6, 12, 6, 12] }, {}, {}, {}, {}, {}]];

  return {
    pageSize: "A4", pageMargins: [36, 32, 36, 40],
    info: { title: `Purchase order ${po.po_number}`, author: COMPANY.name },
    footer: (cp: number, pc: number) => ({ margin: [36, 0, 36, 0], columns: [
      { text: `${COMPANY.name} · ABN ${COMPANY.abn} · ${s.company_depot_address ?? ""} · ${PO_INVOICES_EMAIL}`, fontSize: 7.5, color: C.dim },
      { text: `${po.po_number} · Page ${cp} of ${pc}`, fontSize: 7.5, color: C.dim, alignment: "right" },
    ] }),
    content: [
      { columns: [
        { width: "*", stack: [
          logoDataUrl ? { image: logoDataUrl, width: 100, margin: [0, 0, 0, 4] } : { text: "CORETEL", fontSize: 22, bold: true, color: C.primary, margin: [0, 0, 0, 4] },
          { text: COMPANY.name, fontSize: 9.5, bold: true, color: C.ink },
          { text: `${s.company_depot_address ?? COMPANY.address}  ·  ABN ${COMPANY.abn}  ·  ${PO_INVOICES_EMAIL}`, fontSize: 8.5, color: C.muted },
        ] },
        { width: 230, stack: [
          { text: "PURCHASE ORDER", fontSize: 18, bold: true, color: C.primary, alignment: "right", characterSpacing: 1 },
          { table: { widths: [70, "*"], body: [
            [{ text: "Order no.", fontSize: 8.5, color: C.muted, border: [false, false, false, false] }, { text: po.po_number, fontSize: 12, bold: true, color: C.ink, alignment: "right", border: [false, false, false, false] }],
            [{ text: "Date", fontSize: 8.5, color: C.muted, border: [false, false, false, false] }, { text: fmtDate(po.po_date ?? po.sent_at), fontSize: 10, color: C.text, alignment: "right", border: [false, false, false, false] }],
            [{ text: "Status", fontSize: 8.5, color: C.muted, border: [false, false, false, false] }, { text: status, fontSize: 9, bold: true, color: status === "APPROVED" ? "#1a7f37" : C.muted, alignment: "right", border: [false, false, false, false] }],
          ] }, margin: [0, 6, 0, 0] },
        ] },
      ] },
      { canvas: [{ type: "line", x1: 0, y1: 0, x2: 523, y2: 0, lineWidth: 1.2, lineColor: C.primary }], margin: [0, 8, 0, 10] },
      { columns: [ { width: "*", ...box("Supplier", supplierLines) }, { width: 12, text: "" }, { width: "*", ...box("Deliver to", deliverLines) } ] },
      { table: { widths: ["*"], body: [[{ text: `TAX INVOICES TO: ${COMPANY.name} — ${PO_INVOICES_EMAIL} — quote this PO number on the invoice`, fontSize: 9, bold: true, color: C.ink, alignment: "center", fillColor: C.wash, margin: [6, 5, 6, 5] }]] }, layout: "noBorders", margin: [0, 8, 0, 6] },
      { table: { widths: ["*", "*", "*", "*", "*"], body: [
        [hd("Project"), hd("Site"), hd("Order date"), hd("Deliver by"), hd("Payment terms")],
        [cell(projectRef), cell(siteRef), cell(fmtDate(po.po_date ?? po.sent_at)), cell(po.required_by_date ? fmtDate(po.required_by_date) : "As agreed"), cell(po.payment_terms_text?.trim() || s.po_default_payment_terms || "30 days from invoice")],
      ] }, layout: grid },
      { margin: [0, 10, 0, 0], table: { headerRows: 1, widths: [18, "*", 40, 36, 70, 80], body: [
        [th("#"), th("Description"), th("Qty", { alignment: "right" }), th("Unit"), th("Unit price", { alignment: "right" }), th("Amount ex GST", { alignment: "right" })],
        ...lineRows,
      ] }, layout: { hLineColor: () => C.rule, vLineWidth: () => 0, hLineWidth: (i: number) => i <= 1 ? 0 : 0.5 } },
      { columns: [
        { width: "*", stack: po.scope_text?.trim() ? [
          { text: "SCOPE", fontSize: 8, bold: true, color: C.primary, characterSpacing: 1, margin: [0, 8, 0, 4] },
          { text: po.scope_text.trim(), fontSize: 9.5, color: C.text, lineHeight: 1.3 },
        ] : [{ text: "" }] },
        { width: 20, text: "" },
        { width: 210, margin: [0, 6, 0, 0], table: { widths: ["*", 90], body: [
          [{ text: "Net", fontSize: 9.5, color: C.muted, margin: [8, 5, 8, 3], border: [false, false, false, false] }, { text: fmtMoney(po.subtotal_ex_gst), fontSize: 10, alignment: "right", margin: [8, 5, 8, 3], border: [false, false, false, false] }],
          [{ text: "GST 10%", fontSize: 9.5, color: C.muted, margin: [8, 3, 8, 5], border: [false, false, false, true], borderColor: ["#fff", "#fff", "#fff", C.ruleDark] }, { text: fmtMoney(po.gst_amount), fontSize: 10, alignment: "right", margin: [8, 3, 8, 5], border: [false, false, false, true], borderColor: ["#fff", "#fff", "#fff", C.ruleDark] }],
          [{ text: "TOTAL INC GST", fontSize: 10, bold: true, color: C.primary, fillColor: C.wash, margin: [8, 7, 8, 7], border: [false, false, false, false] }, { text: fmtMoney(po.total_inc_gst), fontSize: 13, bold: true, color: C.primary, fillColor: C.wash, alignment: "right", margin: [8, 7, 8, 7], border: [false, false, false, false] }],
        ] } },
      ] },
      ...(po.notes_text?.trim() ? [{ text: "NOTES", fontSize: 8, bold: true, color: C.primary, characterSpacing: 1, margin: [0, 10, 0, 4] }, { text: po.notes_text.trim(), fontSize: 9.5, color: C.text, lineHeight: 1.3 }] : []),
      { margin: [0, 12, 0, 0], unbreakable: true, stack: [
        { table: { widths: ["*", "*", "*"], body: [
          [hd("Raised by"), hd("Coretel contact for this order"), hd("Insurances")],
          [cell(`${raisedBy}${po.approved_at || po.sent_at ? ` · ${fmtDate(po.approved_at ?? po.sent_at)}` : ""}`, { fontSize: 8.5 }),
           cell([po.coretel_contact_name, po.coretel_contact_phone].filter(Boolean).join(" · ") || raisedBy, { fontSize: 8.5 }),
           cell(s.po_insurances_line || "Public liability, workers comp and plant insurance held by the supplier; certificates on request.", { fontSize: 8.5 })],
        ] }, layout: grid },
        { text: s.po_terms_line || "", fontSize: 7.5, color: C.muted, lineHeight: 1.25, margin: [0, 8, 0, 0] },
      ] },
    ],
  };
}

// -----------------------------------------------------------------------------
// Load everything the PDF prints
// -----------------------------------------------------------------------------
async function loadPo(poId: string): Promise<PoFull> {
  const { data: poRow, error: poErr } = await (supabase as any).from("purchase_orders").select("*").eq("po_id", poId).maybeSingle();
  if (poErr || !poRow) throw new Error(`Could not load PO: ${poErr?.message ?? "not found"}`);
  const [supplierRes, projectRes, linesRes, settings] = await Promise.all([
    poRow.supplier_id ? supabase.from("suppliers").select("*").eq("supplier_id", poRow.supplier_id).maybeSingle() : Promise.resolve({ data: null } as any),
    poRow.project_id ? supabase.from("projects").select("project_code, project_name, site_id").eq("project_id", poRow.project_id).maybeSingle() : Promise.resolve({ data: null } as any),
    supabase.from("purchase_order_lines").select("description, quantity, unit, unit_price_ex_gst, line_total_ex_gst, sort_order").eq("po_id", poId).order("sort_order"),
    loadPoSettings(),
  ]);
  const siteId = poRow.site_id ?? projectRes.data?.site_id ?? null;
  const siteRes = siteId ? await supabase.from("sites").select("site_code, site_name, address_line1, address_line2, suburb, state, postcode").eq("site_id", siteId).maybeSingle() : { data: null };
  return { ...(poRow as any), supplier: supplierRes.data, project: projectRes.data, site: siteRes.data, lines: (linesRes.data ?? []) as PoLine[], settings };
}

/**
 * Make the PDF, save it to storage (versioned), stamp the PO, return a signed
 * URL. The worker attaches pdf_storage_path to the email on send.
 */
export async function generatePoPdf(poId: string, raisedBy: { name: string }): Promise<GeneratePoPdfResult> {
  const po = await loadPo(poId);
  const logoDataUrl = await fetchLogoDataUrl("/coretel-logo-full.png");
  const docDef = buildDocDefinition(po, logoDataUrl, raisedBy.name);
  const pdfMake = await getPdfMake();
  const blob: Blob = await new Promise((resolve, reject) => {
    const t = setTimeout(() => reject(new Error("PDF render timed out")), 20000);
    try { pdfMake.createPdf(docDef).getBlob((b: Blob) => { clearTimeout(t); b ? resolve(b) : reject(new Error("pdfmake returned an empty blob")); }); }
    catch (e: any) { clearTimeout(t); reject(new Error(`pdfmake render failed: ${e?.message ?? e}`)); }
  });
  const nextVersion = (po.pdf_version ?? 0) + 1;
  const safeNumber = String(po.po_number).replace(/[^A-Za-z0-9_-]/g, "-");
  const storagePath = `${po.po_id}/${safeNumber}_v${nextVersion}.pdf`;
  const { error: upErr } = await supabase.storage.from("po-pdfs").upload(storagePath, blob, { contentType: "application/pdf", upsert: true, cacheControl: "3600" });
  if (upErr) throw new Error(`PDF upload failed: ${upErr.message}`);
  const { data: authData } = await supabase.auth.getUser();
  await (supabase as any).from("purchase_orders").update({ pdf_storage_path: storagePath, pdf_version: nextVersion, pdf_generated_at: new Date().toISOString(), pdf_generated_by: authData?.user?.id ?? null }).eq("po_id", poId);
  const { data: signed, error: sErr } = await supabase.storage.from("po-pdfs").createSignedUrl(storagePath, 3600);
  if (sErr || !signed) throw new Error(`Could not sign the PDF URL: ${sErr?.message ?? "unknown"}`);
  return { signedUrl: signed.signedUrl, storagePath, version: nextVersion };
}

/** Kept for anything that still calls it: make + open in a new tab. */
export async function downloadPoPdf(poId: string, issuedBy: { name: string }): Promise<void> {
  const r = await generatePoPdf(poId, issuedBy);
  window.open(r.signedUrl, "_blank", "noopener");
}

/** Signed URL for the latest saved PDF, or null. */
export async function getLatestPoPdfUrl(poId: string): Promise<string | null> {
  const { data } = await (supabase as any).from("purchase_orders").select("pdf_storage_path").eq("po_id", poId).maybeSingle();
  if (!data?.pdf_storage_path) return null;
  const { data: signed } = await supabase.storage.from("po-pdfs").createSignedUrl(data.pdf_storage_path, 3600);
  return signed?.signedUrl ?? null;
}
